// QA R3-10 — single client-side predicate for "is a payment still owed?".
//
// A ticket created with a $0 total stays `paymentStatus: 'unpaid'` in the DB, but
// the server's `isPaymentSatisfied` gate already lets it through (retrieval,
// delivery) and refuses cash on it. DTOs therefore carry an additive
// `paymentRequired` flag computed by the server; every UI derives its badge and
// its cash/comp actions from this one function instead of re-implementing
// `total === 0` checks.

export type PaymentDisplayState = 'paid' | 'due' | 'no_charge';

export interface PaymentDueInput {
  paymentStatus?: 'unpaid' | 'paid';
  /** Server-computed; `false` only when unpaid AND the total is $0. Absent = required. */
  paymentRequired?: boolean;
}

/**
 * True only when the ticket is unpaid AND the server says something is owed.
 * An absent `paymentRequired` (older server, stale offline cache) keeps the
 * pre-existing behaviour: unpaid means due.
 */
export function isPaymentDue(input: PaymentDueInput): boolean {
  return input.paymentStatus === 'unpaid' && input.paymentRequired !== false;
}

/**
 * 'paid' | 'due' | 'no_charge' (unpaid, nothing owed), or `null` when the DTO
 * carries no payment status at all (nothing to render).
 */
export function paymentDisplayState(input: PaymentDueInput): PaymentDisplayState | null {
  if (!input.paymentStatus) return null;
  if (input.paymentStatus === 'paid') return 'paid';
  return isPaymentDue(input) ? 'due' : 'no_charge';
}
